/**
 * CSS-in-JS Feedback Styles
 * 
 * Provides comprehensive styling for visual feedback states including
 * validation indicators, error messages, icons, and animations.
 * Works with any Webflow theme without interfering with existing styles.
 */

import { FeedbackState } from '../feedback/visual-feedback';

/**
 * Color palette for feedback states
 */
export const FeedbackColors = {
  // Error colors
  error: {
    primary: '#E53E3E',
    secondary: '#FED7D7',
    border: '#FC8181',
    background: '#FFF5F5',
    text: '#742A2A'
  },
  
  // Warning colors
  warning: {
    primary: '#D69E2E',
    secondary: '#FAECC2',
    border: '#F6D55C',
    background: '#FFFAF0',
    text: '#744210'
  },
  
  // Success colors
  success: {
    primary: '#38A169',
    secondary: '#C6F6D5',
    border: '#68D391',
    background: '#F0FFF4',
    text: '#1A202C'
  },
  
  // Focus colors
  focus: {
    primary: '#3182CE',
    secondary: '#BEE3F8',
    border: '#63B3ED',
    background: '#EBF8FF',
    glow: 'rgba(49, 130, 206, 0.4)'
  },
  
  // Loading colors
  loading: {
    primary: '#718096',
    secondary: '#E2E8F0',
    border: '#A0AEC0',
    background: '#F7FAFC'
  },
  
  // Neutral colors
  neutral: {
    text: '#2D3748',
    textLight: '#4A5568',
    border: '#E2E8F0',
    background: '#FFFFFF'
  }
} as const;

/**
 * Typography styles for feedback
 */
export const FeedbackTypography = {
  message: {
    fontSize: '14px',
    lineHeight: '1.4',
    fontWeight: '400',
    fontFamily: 'inherit'
  },
  
  messageSmall: {
    fontSize: '12px',
    lineHeight: '1.3',
    fontWeight: '400',
    fontFamily: 'inherit'
  },
  
  icon: {
    fontSize: '16px',
    lineHeight: '1',
    fontWeight: 'normal',
    fontFamily: 'inherit'
  }
} as const;

/**
 * Spacing and sizing values
 */
export const FeedbackSpacing = {
  // Padding
  messagePadding: '8px 12px',
  messageSmallPadding: '6px 10px',
  iconPadding: '4px',
  
  // Margins
  messageMarginTop: '6px',
  messageMarginBottom: '4px',
  iconMarginLeft: '8px',
  
  // Border radius
  borderRadius: '4px',
  borderRadiusSmall: '3px',
  
  // Border width
  borderWidth: '1px',
  borderWidthThick: '2px',
  
  // Icon sizes
  iconSize: '16px',
  iconSizeSmall: '14px'
} as const;

/**
 * Animation durations and easings
 */
export const FeedbackAnimations = {
  duration: {
    fast: '150ms',
    normal: '250ms',
    slow: '350ms'
  },
  
  easing: {
    ease: 'ease',
    easeIn: 'ease-in',
    easeOut: 'ease-out',
    easeInOut: 'ease-in-out'
  }
} as const;

/**
 * CSS style object type
 */
type CSSStyleObject = {
  [K in keyof CSSStyleDeclaration]?: string | number;
} & {
  [key: string]: string | number | undefined;
};

/**
 * Style generator for different feedback components
 */
export class FeedbackStyles {
  private classPrefix: string;
  private stylesInjected: boolean = false;

  constructor(classPrefix: string = 'formatting') {
    this.classPrefix = classPrefix;
  }

  /**
   * Update CSS class prefix
   */
  public updatePrefix(newPrefix: string): void {
    this.classPrefix = newPrefix;
    this.stylesInjected = false; // Force re-injection with new prefix
  }

  /**
   * Get CSS class for feedback state
   */
  public getStateClass(state: FeedbackState): string {
    return `${this.classPrefix}-field-${state}`;
  }

  /**
   * Get CSS class for message type
   */
  public getMessageClass(type: 'error' | 'warning' | 'success' | 'info'): string {
    return `${this.classPrefix}-message--${type}`;
  }

  /**
   * Get CSS class for icon type
   */
  public getIconClass(type: 'error' | 'warning' | 'success' | 'info'): string {
    return `${this.classPrefix}-icon--${type}`;
  }

  /**
   * Inject all styles into DOM
   */
  public injectStyles(): void {
    if (this.stylesInjected) {
      return;
    }

    const styleSheet = this.createStyleSheet();
    document.head.appendChild(styleSheet);
    this.stylesInjected = true;
  }

  /**
   * Remove injected styles from DOM
   */
  public removeStyles(): void {
    const existingStyle = document.getElementById(`${this.classPrefix}-feedback-styles`);
    if (existingStyle) {
      existingStyle.remove();
      this.stylesInjected = false;
    }
  }

  /**
   * Create complete stylesheet
   */
  private createStyleSheet(): HTMLStyleElement {
    const style = document.createElement('style');
    style.id = `${this.classPrefix}-feedback-styles`;
    style.textContent = this.generateAllStyles();
    return style;
  }

  /**
   * Generate all CSS styles as string
   */
  private generateAllStyles(): string {
    return [
      this.generateFieldStateStyles(),
      this.generateMessageStyles(),
      this.generateIconStyles(),
      this.generateAnimationStyles(),
      this.generateUtilityStyles(),
      this.generateAccessibilityStyles()
    ].join('\n\n');
  }

  /**
   * Generate field state styles
   */
  private generateFieldStateStyles(): string {
    return `
/* Field State Styles */
.${this.classPrefix}-field-default {
  transition: border-color ${FeedbackAnimations.duration.normal} ${FeedbackAnimations.easing.easeInOut},
              box-shadow ${FeedbackAnimations.duration.normal} ${FeedbackAnimations.easing.easeInOut};
}

.${this.classPrefix}-field-focus {
  border-color: ${FeedbackColors.focus.border} !important;
  box-shadow: 0 0 0 3px ${FeedbackColors.focus.glow} !important;
  outline: none !important;
}

.${this.classPrefix}-field-invalid {
  border-color: ${FeedbackColors.error.border} !important;
  background-color: ${FeedbackColors.error.background} !important;
  box-shadow: 0 0 0 1px ${FeedbackColors.error.primary} !important;
}

.${this.classPrefix}-field-valid {
  border-color: ${FeedbackColors.success.border} !important;
  background-color: ${FeedbackColors.success.background} !important;
}

.${this.classPrefix}-field-warning {
  border-color: ${FeedbackColors.warning.border} !important;
  background-color: ${FeedbackColors.warning.background} !important;
}

.${this.classPrefix}-field-loading {
  border-color: ${FeedbackColors.loading.border} !important;
  background-color: ${FeedbackColors.loading.background} !important;
  opacity: 0.8;
}

/* Field wrapper styles */
.${this.classPrefix}-field-wrapper {
  position: relative !important;
  display: inline-block !important;
  width: 100% !important;
}`;
  }

  /**
   * Generate message styles
   */
  private generateMessageStyles(): string {
    return `
/* Message Styles */
.${this.classPrefix}-message {
  display: block;
  margin-top: ${FeedbackSpacing.messageMarginTop};
  margin-bottom: ${FeedbackSpacing.messageMarginBottom};
  padding: ${FeedbackSpacing.messagePadding};
  border-radius: ${FeedbackSpacing.borderRadius};
  font-size: ${FeedbackTypography.message.fontSize};
  line-height: ${FeedbackTypography.message.lineHeight};
  font-weight: ${FeedbackTypography.message.fontWeight};
  font-family: ${FeedbackTypography.message.fontFamily};
  transition: opacity ${FeedbackAnimations.duration.normal} ${FeedbackAnimations.easing.easeInOut};
}

.${this.classPrefix}-message--error {
  color: ${FeedbackColors.error.text};
  background-color: ${FeedbackColors.error.secondary};
  border: ${FeedbackSpacing.borderWidth} solid ${FeedbackColors.error.border};
}

.${this.classPrefix}-message--warning {
  color: ${FeedbackColors.warning.text};
  background-color: ${FeedbackColors.warning.secondary};
  border: ${FeedbackSpacing.borderWidth} solid ${FeedbackColors.warning.border};
}

.${this.classPrefix}-message--success {
  color: ${FeedbackColors.success.text};
  background-color: ${FeedbackColors.success.secondary};
  border: ${FeedbackSpacing.borderWidth} solid ${FeedbackColors.success.border};
}

.${this.classPrefix}-message--info {
  color: ${FeedbackColors.neutral.text};
  background-color: ${FeedbackColors.neutral.background};
  border: ${FeedbackSpacing.borderWidth} solid ${FeedbackColors.neutral.border};
}

/* Small message variant */
.${this.classPrefix}-message--small {
  padding: ${FeedbackSpacing.messageSmallPadding};
  font-size: ${FeedbackTypography.messageSmall.fontSize};
  line-height: ${FeedbackTypography.messageSmall.lineHeight};
}

/* Inline container for messages */
.${this.classPrefix}-inline-container {
  display: inline-block;
  width: 100%;
}`;
  }

  /**
   * Generate icon styles
   */
  private generateIconStyles(): string {
    return `
/* Icon Styles */
.${this.classPrefix}-icon {
  display: inline-block;
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: ${FeedbackSpacing.iconSize};
  height: ${FeedbackSpacing.iconSize};
  padding: ${FeedbackSpacing.iconPadding};
  font-size: ${FeedbackTypography.icon.fontSize};
  line-height: ${FeedbackTypography.icon.lineHeight};
  text-align: center;
  pointer-events: none;
  user-select: none;
  z-index: 10;
}

.${this.classPrefix}-icon--error {
  color: ${FeedbackColors.error.primary};
}

.${this.classPrefix}-icon--warning {
  color: ${FeedbackColors.warning.primary};
}

.${this.classPrefix}-icon--success {
  color: ${FeedbackColors.success.primary};
}

.${this.classPrefix}-icon--info {
  color: ${FeedbackColors.neutral.textLight};
}

/* Small icon variant */
.${this.classPrefix}-icon--small {
  width: ${FeedbackSpacing.iconSizeSmall};
  height: ${FeedbackSpacing.iconSizeSmall};
  right: 8px;
}`;
  }

  /**
   * Generate animation styles
   */
  private generateAnimationStyles(): string {
    return `
/* Animation Styles */
@keyframes ${this.classPrefix}-shake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-4px); }
  40% { transform: translateX(4px); }
  60% { transform: translateX(-4px); }
  80% { transform: translateX(4px); }
}

@keyframes ${this.classPrefix}-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.02); }
}

@keyframes ${this.classPrefix}-fade-in {
  from { opacity: 0; transform: translateY(-5px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes ${this.classPrefix}-glow {
  0%, 100% { box-shadow: 0 0 0 0 transparent; }
  50% { box-shadow: 0 0 0 4px rgba(49, 130, 206, 0.3); }
}

/* Animation classes */
.${this.classPrefix}-animate-shake {
  animation: ${this.classPrefix}-shake 0.5s ease-in-out;
}

.${this.classPrefix}-animate-pulse {
  animation: ${this.classPrefix}-pulse 1s ease-in-out infinite alternate;
}

.${this.classPrefix}-animate-fade-in {
  animation: ${this.classPrefix}-fade-in 0.3s ease-out;
}

.${this.classPrefix}-animate-glow {
  animation: ${this.classPrefix}-glow 0.8s ease-out;
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .${this.classPrefix}-field-default,
  .${this.classPrefix}-field-focus,
  .${this.classPrefix}-field-invalid,
  .${this.classPrefix}-field-valid,
  .${this.classPrefix}-field-warning,
  .${this.classPrefix}-field-loading,
  .${this.classPrefix}-message,
  .${this.classPrefix}-icon {
    transition: none !important;
    animation: none !important;
  }
}`;
  }

  /**
   * Generate utility styles
   */
  private generateUtilityStyles(): string {
    return `
/* Utility Styles */
.${this.classPrefix}-sr-only {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

.${this.classPrefix}-hidden {
  display: none !important;
}

.${this.classPrefix}-visually-hidden {
  position: absolute !important;
  left: -10000px !important;
  top: auto !important;
  width: 1px !important;
  height: 1px !important;
  overflow: hidden !important;
}

/* Tooltip positioning */
.${this.classPrefix}-tooltip {
  position: absolute;
  z-index: 1000;
  padding: 8px 12px;
  background-color: rgba(0, 0, 0, 0.9);
  color: white;
  border-radius: 4px;
  font-size: 14px;
  line-height: 1.4;
  max-width: 250px;
  word-wrap: break-word;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.${this.classPrefix}-tooltip::after {
  content: '';
  position: absolute;
  bottom: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: transparent transparent rgba(0, 0, 0, 0.9) transparent;
}`;
  }

  /**
   * Generate accessibility styles
   */
  private generateAccessibilityStyles(): string {
    return `
/* Accessibility Styles */
.${this.classPrefix}-field-invalid:focus,
.${this.classPrefix}-field-valid:focus,
.${this.classPrefix}-field-warning:focus {
  outline: 2px solid ${FeedbackColors.focus.primary} !important;
  outline-offset: 2px !important;
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .${this.classPrefix}-field-invalid {
    border-color: #ff0000 !important;
    background-color: #ffe6e6 !important;
  }
  
  .${this.classPrefix}-field-valid {
    border-color: #00aa00 !important;
    background-color: #e6ffe6 !important;
  }
  
  .${this.classPrefix}-field-warning {
    border-color: #ff8800 !important;
    background-color: #fff2e6 !important;
  }
  
  .${this.classPrefix}-message--error {
    color: #990000 !important;
    background-color: #ffcccc !important;
    border-color: #cc0000 !important;
  }
  
  .${this.classPrefix}-message--success {
    color: #006600 !important;
    background-color: #ccffcc !important;
    border-color: #009900 !important;
  }
  
  .${this.classPrefix}-message--warning {
    color: #cc6600 !important;
    background-color: #ffe6cc !important;
    border-color: #ff8800 !important;
  }
}

/* Focus visible for keyboard navigation */
.${this.classPrefix}-message:focus-visible,
.${this.classPrefix}-icon:focus-visible {
  outline: 2px solid ${FeedbackColors.focus.primary};
  outline-offset: 2px;
}

/* Print styles */
@media print {
  .${this.classPrefix}-field-invalid,
  .${this.classPrefix}-field-valid,
  .${this.classPrefix}-field-warning {
    border: 2px solid #000 !important;
    background: transparent !important;
    box-shadow: none !important;
  }
  
  .${this.classPrefix}-message {
    border: 1px solid #000 !important;
    background: transparent !important;
    color: #000 !important;
  }
  
  .${this.classPrefix}-icon {
    display: none !important;
  }
}`;
  }

  /**
   * Generate inline styles for dynamic styling
   */
  public generateInlineStyles(state: FeedbackState): CSSStyleObject {
    const baseStyles: CSSStyleObject = {
      transition: `border-color ${FeedbackAnimations.duration.normal} ${FeedbackAnimations.easing.easeInOut}, box-shadow ${FeedbackAnimations.duration.normal} ${FeedbackAnimations.easing.easeInOut}`
    };

    const stateStyles: Record<FeedbackState, CSSStyleObject> = {
      [FeedbackState.DEFAULT]: baseStyles,
      
      [FeedbackState.FOCUS]: {
        ...baseStyles,
        borderColor: FeedbackColors.focus.border,
        boxShadow: `0 0 0 3px ${FeedbackColors.focus.glow}`,
        outline: 'none'
      },
      
      [FeedbackState.INVALID]: {
        ...baseStyles,
        borderColor: FeedbackColors.error.border,
        backgroundColor: FeedbackColors.error.background,
        boxShadow: `0 0 0 1px ${FeedbackColors.error.primary}`
      },
      
      [FeedbackState.VALID]: {
        ...baseStyles,
        borderColor: FeedbackColors.success.border,
        backgroundColor: FeedbackColors.success.background
      },
      
      [FeedbackState.WARNING]: {
        ...baseStyles,
        borderColor: FeedbackColors.warning.border,
        backgroundColor: FeedbackColors.warning.background
      },
      
      [FeedbackState.LOADING]: {
        ...baseStyles,
        borderColor: FeedbackColors.loading.border,
        backgroundColor: FeedbackColors.loading.background,
        opacity: '0.8'
      }
    };

    return stateStyles[state] || baseStyles;
  }

  /**
   * Apply inline styles to element
   */
  public applyInlineStyles(element: HTMLElement, state: FeedbackState): void {
    const styles = this.generateInlineStyles(state);
    
    Object.keys(styles).forEach(property => {
      const value = styles[property];
      if (value !== undefined) {
        (element.style as any)[property] = value;
      }
    });
  }

  /**
   * Remove inline styles from element
   */
  public removeInlineStyles(element: HTMLElement): void {
    const stylesToRemove = [
      'borderColor',
      'backgroundColor',
      'boxShadow',
      'outline',
      'opacity',
      'transition'
    ];
    
    stylesToRemove.forEach(property => {
      (element.style as any)[property] = '';
    });
  }
}